import React, { useEffect, useRef, useState } from 'react';
import { IoChevronDown } from 'react-icons/io5';
import { useAssistant } from '../context/AssistantContext';
import { ChatMessages } from './ChatMessages';

interface ChatbotProps {}

export const Chatbot: React.FC<ChatbotProps> = () => {
    const { state, method } = useAssistant();
    const { messages, selectedProduct, egressConsentState, isBlending } = state;

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const [showScrollButton, setShowScrollButton] = useState(false);

    // Check if user has scrolled up using window scroll position
    const handleScroll = () => {
        const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const windowScrollHeight = document.documentElement.scrollHeight;
        const windowClientHeight = window.innerHeight;
        const isNearBottom = windowScrollHeight - windowScrollTop - windowClientHeight < 100;
        setShowScrollButton(!isNearBottom);
    };

    // Scroll to bottom function
    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    // Auto-scroll to bottom when new messages are added
    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Add window scroll listener
    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <div className="flex h-full gap-4">
            {/* Chat Container - Left Side */}
            <div className="flex-1 flex flex-col pt-6 px-6 h-full">
                <div className='bg-white rounded-lg p-5 relative'>
                    <ChatMessages
                        messages={messages}
                        selectedProduct={selectedProduct}
                        validationRules={state?.assistantConfig?.validationRules ?? []}
                        egressConsentState={egressConsentState}
                        onEgressConsentAccept={method.acceptEgressConsent}
                        onEgressConsentReject={method.rejectEgressConsent}
                        handleSingleSelect={method.handleSingleSelect}
                        updateSingleSelect={method.updateSingleSelect}
                        isBlending={isBlending}
                        handleMultipleChoice={method.handleMultipleChoice}
                        updateMultipleChoice={method.updateMultipleChoice}
                        handleFormSubmit={method.handleFormSubmit}
                        updateFormSubmit={method.updateFormSubmit}
                        handleTextInput={method.handleTextInput}
                        updateTextInput={method.updateTextInput}
                    />
                    {/* Invisible element at the end for auto-scroll */}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Scroll to bottom button */}
            {showScrollButton && (
                <button
                    onClick={scrollToBottom}
                    className='fixed bottom-24 z-40 right-8 w-12 h-12 bg-primary-500 hover:bg-primary-500 text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-200 hover:scale-110'
                    aria-label="Scroll to bottom"
                >
                    <IoChevronDown className="text-2xl" />
                </button>
            )}
        </div>
    );
};
